import {
  ScheduleFrequencies,
  ScheduleEntryStatuses,
  reportScheduleByProjectForUserQuery,
} from '@aims/shared/report-schedules/constants';
import { useQuery } from '@apollo/client';
import { Button, Empty, Skeleton, Table, Tag } from 'antd';
import Column from 'antd/lib/table/Column';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';

function ScheduledReportsList({ projectId }) {
  const { data: scheduleData, loading } = useQuery(
    reportScheduleByProjectForUserQuery,
    {
      variables: { holisticProjectId: projectId },
      skip: !projectId,
      fetchPolicy: 'network-only',
    },
  );

  const schedule = useMemo(
    () => scheduleData?.reportScheduleByProjectForUser || null,
    [scheduleData],
  );

  const entries = useMemo(() => schedule?.entries || [], [schedule]);

  if (loading) {
    return <Skeleton active />;
  }

  if (!schedule) {
    return (
      <Empty description="No report schedule has been set up for this project yet." />
    );
  }

  return (
    <div style={{ padding: '16px 0' }}>
      <Table
        dataSource={entries}
        rowKey="entryId"
        size="small"
        pagination={{ pageSize: 10, showSizeChanger: true }}
      >
        <Column title="Label" dataIndex="label" key="label" width={150} />
        <Column
          title="Frequency"
          dataIndex="frequency"
          key="frequency"
          width={110}
          render={(frequency) => (
            <Tag color={ScheduleFrequencies[frequency]?.color || 'default'}>
              {ScheduleFrequencies[frequency]?.label || frequency}
            </Tag>
          )}
        />
        <Column
          title="Due Date"
          dataIndex="dueDate"
          key="dueDate"
          width={140}
          render={(date) => (date ? dayjs(date).format('MMM D, YYYY') : '-')}
        />
        <Column
          title="Status"
          dataIndex="status"
          key="status"
          width={110}
          render={(status) => (
            <Tag color={ScheduleEntryStatuses[status]?.color || 'default'}>
              {ScheduleEntryStatuses[status]?.label || status}
            </Tag>
          )}
        />
        <Column
          title=""
          key="actions"
          width={80}
          render={(_, record) => {
            if (
              record.scheduledReportId &&
              (record.status === 'SUBMITTED' || record.status === 'APPROVED')
            ) {
              return (
                <Link
                  to={`/holistic-projects/${projectId}/scheduled-reports/view/${record.scheduledReportId}`}
                >
                  <Button type="link" icon={<EyeOutlined />} size="small">
                    View
                  </Button>
                </Link>
              );
            }
            if (
              record.status === 'DUE' ||
              record.status === 'OVERDUE' ||
              record.status === 'UPCOMING'
            ) {
              return (
                <Link
                  to={`/holistic-projects/${projectId}/scheduled-reports/edit/${record.entryId}?scheduleId=${schedule._id}`}
                >
                  <Button type="link" icon={<EditOutlined />} size="small">
                    Fill Out
                  </Button>
                </Link>
              );
            }
            return null;
          }}
        />
      </Table>
    </div>
  );
}

export default ScheduledReportsList;
